import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  ArrowRight, MessageSquare, Calendar, BookOpen, Home, CheckCircle2,
  Dumbbell, Zap, Activity, Target, Brain, Moon, Heart, Shield, Search,
  ChevronRight, Star, Sparkles
} from 'lucide-react';
import ScheduleModal from '../components/ScheduleModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const THERAPY_DATA = {
  'weight-loss': {
    title: 'Medical Weight Loss',
    subtitle: 'Semaglutide, Tirzepatide & GLP-1 Programs',
    description: 'Evidence-based weight management with GLP-1 medications, supported by personalized protocols and ongoing medical oversight.',
    icon: Dumbbell,
    color: 'emerald',
    gradient: 'from-emerald-500/20 to-green-500/10',
    border: 'border-emerald-500/30',
    accent: 'text-emerald-400',
    heroLine: 'Lose weight with science, not willpower.',
    therapies: [
      { name: 'Semaglutide (Ozempic/Wegovy)', desc: 'GLP-1 receptor agonist that reduces appetite and improves blood sugar regulation. Average weight loss of 15-17% body weight.', highlight: 'Most studied GLP-1 option' },
      { name: 'Tirzepatide (Mounjaro/Zepbound)', desc: 'Dual GLP-1/GIP agonist with enhanced fat metabolism and insulin sensitivity. Average weight loss of 20-22% body weight.', highlight: 'Strongest clinical results' },
      { name: 'Compounded GLP-1 Options', desc: 'Same active ingredients prepared by licensed compounding pharmacies at more accessible price points.', highlight: 'More accessible pricing' },
    ],
    benefits: ['Clinically proven 15-22% body weight reduction', 'Reduced appetite and food noise', 'Improved insulin sensitivity and blood sugar', 'Licensed medical provider oversight', 'Personalized dosing and monitoring'],
    whoIsFor: ['Adults with BMI over 27 struggling with weight', 'Those who have tried diet and exercise without lasting results', 'People with insulin resistance or type 2 diabetes', 'Anyone wanting medical-grade weight management support'],
    blogKeywords: ['ozempic', 'semaglutide', 'tirzepatide', 'wegovy', 'glp-1', 'glp1', 'weight-loss', 'plateau', 'appetite'],
    ctaText: 'See Your Weight Loss Forecast',
    forecastGoal: 'weight-loss',
    metaTitle: 'Medical Weight Loss Programs | Bryan 2.0',
    metaDescription: 'Semaglutide, Tirzepatide, and compounded GLP-1 weight loss programs with personalized protocols and medical oversight.',
  },
  'energy': {
    title: 'Energy & Cellular Health',
    subtitle: 'NAD+, MOTS-c & Mitochondrial Support',
    description: 'Restore cellular energy production and fight fatigue at its source with targeted mitochondrial therapies.',
    icon: Zap,
    color: 'cyan',
    gradient: 'from-cyan-500/20 to-blue-500/10',
    border: 'border-cyan-500/30',
    accent: 'text-cyan-400',
    heroLine: 'Energy that comes from your cells, not caffeine.',
    therapies: [
      { name: 'NAD+ Therapy', desc: 'Restores the essential coenzyme that powers every cell in your body. Supports energy production, cognitive clarity, and cellular repair.', highlight: 'Foundational anti-aging therapy' },
      { name: 'MOTS-c Peptide', desc: 'A mitochondrial-derived peptide that acts as an exercise mimetic, enhancing metabolism and creating new mitochondria.', highlight: 'Exercise mimetic' },
      { name: 'Mitochondrial Stack', desc: 'NAD+ combined with MOTS-c and supporting nutrients for comprehensive cellular energy optimization.', highlight: 'Most complete approach' },
    ],
    benefits: ['Sustained energy without crashes or jitters', 'Improved mental clarity and focus', 'Enhanced exercise performance and recovery', 'Anti-aging at the cellular level', 'Better sleep quality through circadian support'],
    whoIsFor: ['Adults over 40 experiencing energy decline', 'Anyone dealing with persistent fatigue', 'People wanting cognitive enhancement', 'Those on GLP-1 medications needing energy support'],
    blogKeywords: ['nad', 'mots-c', 'mitochondrial', 'energy', 'cellular'],
    ctaText: 'Discover Your Energy Path',
    forecastGoal: 'energy',
    metaTitle: 'Energy & Cellular Health: NAD+ Therapy | Bryan 2.0',
    metaDescription: 'NAD+ therapy and mitochondrial support for energy restoration, cognitive clarity, and cellular health optimization.',
  },
  'recovery': {
    title: 'Recovery & Healing',
    subtitle: 'BPC-157, TB-500 & The Wolverine Stack',
    description: 'Targeted peptide protocols that accelerate your body\'s natural healing processes for injuries, gut health, and tissue repair.',
    icon: Activity,
    color: 'orange',
    gradient: 'from-orange-500/20 to-amber-500/10',
    border: 'border-orange-500/30',
    accent: 'text-orange-400',
    heroLine: 'Heal faster. Recover stronger. Stay active.',
    therapies: [
      { name: 'BPC-157', desc: 'Body Protection Compound — supports gut healing, tendon repair, and tissue recovery through angiogenesis and anti-inflammatory mechanisms.', highlight: 'Gut + tissue healer' },
      { name: 'TB-500 (Thymosin Beta-4)', desc: 'Systemic healing peptide that promotes cell migration to injury sites, reduces inflammation, and supports tissue regeneration.', highlight: 'Body-wide recovery' },
      { name: 'The Wolverine Stack', desc: 'BPC-157 + TB-500 combined for synergistic healing — addressing repair from both local and systemic angles.', highlight: 'Most popular recovery protocol' },
    ],
    benefits: ['Accelerated injury recovery', 'Gut lining repair and digestive support', 'Reduced chronic inflammation', 'Enhanced exercise recovery', 'Joint and tendon support'],
    whoIsFor: ['Athletes and active individuals with injuries', 'Post-surgical recovery patients', 'People with chronic gut issues', 'Anyone with joint or tendon pain', 'Those increasing activity on weight loss programs'],
    blogKeywords: ['bpc-157', 'bpc157', 'tb-500', 'tb500', 'wolverine', 'recovery', 'healing', 'peptide'],
    ctaText: 'Explore Recovery Options',
    forecastGoal: 'recovery',
    metaTitle: 'Recovery & Healing Peptides: BPC-157 & TB-500 | Bryan 2.0',
    metaDescription: 'BPC-157, TB-500, and the Wolverine Stack for accelerated recovery, gut healing, and tissue repair.',
  },
  'body-composition': {
    title: 'Body Composition',
    subtitle: 'Tesamorelin, Sermorelin & Growth Hormone Support',
    description: 'Optimize your body composition with growth hormone-stimulating peptides that target visceral fat and support lean muscle.',
    icon: Target,
    color: 'purple',
    gradient: 'from-purple-500/20 to-violet-500/10',
    border: 'border-purple-500/30',
    accent: 'text-purple-400',
    heroLine: 'Reshape your body at the cellular level.',
    therapies: [
      { name: 'Tesamorelin', desc: 'FDA-recognized GHRH analog that stimulates natural growth hormone production. Clinically shown to reduce visceral fat.', highlight: 'Targets visceral fat' },
      { name: 'Sermorelin', desc: 'Growth hormone-releasing peptide that enhances natural GH production for improved sleep, body composition, and recovery.', highlight: 'Sleep + body comp' },
      { name: 'Body Composition Stack', desc: 'Growth hormone support combined with GLP-1 medication for comprehensive body recomposition — less fat, more lean tissue.', highlight: 'Complete recomposition' },
    ],
    benefits: ['Reduced visceral (belly) fat', 'Improved lean muscle mass', 'Enhanced sleep quality', 'Better skin elasticity and appearance', 'Improved metabolic markers'],
    whoIsFor: ['Adults with stubborn visceral fat', 'People focused on body recomposition over just weight loss', 'Those with declining growth hormone', 'Anyone wanting improved sleep and recovery'],
    blogKeywords: ['tesamorelin', 'sermorelin', 'body-composition', 'growth-hormone', 'muscle'],
    ctaText: 'Start Your Body Composition Plan',
    forecastGoal: 'muscle',
    metaTitle: 'Body Composition Optimization: Tesamorelin & Sermorelin | Bryan 2.0',
    metaDescription: 'Tesamorelin and sermorelin for body composition — reduce visceral fat, support lean mass, and optimize growth hormone.',
  },
  'cognitive': {
    title: 'Cognitive Performance',
    subtitle: 'NAD+ & Mitochondrial Brain Support',
    description: 'Sharpen your mental edge with targeted therapies that support focus, memory, and cognitive clarity at the cellular level.',
    icon: Brain,
    color: 'pink',
    gradient: 'from-pink-500/20 to-rose-500/10',
    border: 'border-pink-500/30',
    accent: 'text-pink-400',
    heroLine: 'Clear thinking isn\'t optional. It\'s optimizable.',
    therapies: [
      { name: 'NAD+ for Cognition', desc: 'Restores cellular energy in neurons, activates neuroprotective sirtuins, and supports DNA repair in brain cells. The foundational therapy for cognitive clarity.', highlight: 'Foundational brain support' },
      { name: 'MOTS-c for Brain Metabolism', desc: 'Mitochondrial peptide that enhances cellular energy production in neural tissue, supporting sustained mental performance and metabolic brain health.', highlight: 'Metabolic brain support' },
      { name: 'Cognitive Optimization Protocol', desc: 'NAD+ combined with targeted mitochondrial support for comprehensive cognitive enhancement — cellular energy plus neural protection.', highlight: 'Complete cognitive optimization' },
    ],
    benefits: ['Enhanced focus and concentration', 'Improved memory and recall', 'Reduced brain fog', 'Better mental performance under stress', 'Neuroprotection and long-term brain health'],
    whoIsFor: ['Professionals with demanding cognitive workloads', 'Adults experiencing age-related cognitive changes', 'People with brain fog from any cause', 'Those on GLP-1 medications with cognitive effects'],
    blogKeywords: ['cognitive', 'brain-fog', 'focus', 'mental-clarity'],
    ctaText: 'Optimize Your Cognitive Edge',
    forecastGoal: 'cognitive',
    metaTitle: 'Cognitive Performance: NAD+ & Brain Optimization | Bryan 2.0',
    metaDescription: 'NAD+ and mitochondrial support for cognitive performance — enhance focus, memory, and mental clarity with targeted therapy.',
  },
  'sleep': {
    title: 'Sleep Optimization',
    subtitle: 'NAD+, Sermorelin & Sleep Protocols',
    description: 'Achieve deep, restorative sleep through targeted therapies that support your body\'s natural sleep architecture and overnight recovery.',
    icon: Moon,
    color: 'indigo',
    gradient: 'from-indigo-500/20 to-blue-500/10',
    border: 'border-indigo-500/30',
    accent: 'text-indigo-400',
    heroLine: 'Real sleep. Deep recovery. No grogginess.',
    therapies: [
      { name: 'NAD+ for Circadian Support', desc: 'Regulates circadian rhythm genes and supports the cellular energy needed for healthy sleep-wake cycles. Foundational for sleep quality.', highlight: 'Circadian rhythm support' },
      { name: 'Sermorelin for Sleep', desc: 'Growth hormone-releasing peptide that enhances natural GH production — 70-80% of which occurs during deep sleep. Improves sleep depth and overnight recovery.', highlight: 'Deep sleep + GH release' },
      { name: 'Sleep Optimization Protocol', desc: 'NAD+ for circadian regulation combined with sermorelin for growth hormone support and targeted lifestyle optimization.', highlight: 'Complete sleep optimization' },
    ],
    benefits: ['Deeper, more restorative sleep', 'Improved growth hormone release', 'Better cognitive function and memory consolidation', 'Enhanced recovery and immune function', 'Sustainable without dependency'],
    whoIsFor: ['Anyone struggling with sleep quality', 'People who sleep enough hours but wake unrefreshed', 'Perimenopausal women with sleep disruption', 'Those wanting to optimize sleep and overnight recovery'],
    blogKeywords: ['sleep', 'insomnia', 'tired'],
    ctaText: 'Improve Your Sleep',
    forecastGoal: 'sleep',
    metaTitle: 'Sleep Optimization: NAD+ & Sermorelin | Bryan 2.0',
    metaDescription: 'NAD+ and sermorelin sleep optimization — achieve deep, restorative sleep and enhanced overnight recovery.',
  },
  'hormones': {
    title: "Women's Hormone Optimization",
    subtitle: 'HRT, Nuvella & Personalized Hormone Support',
    description: 'Navigate hormonal transitions with confidence using personalized hormone optimization that addresses the root cause of perimenopausal and menopausal symptoms.',
    icon: Heart,
    color: 'rose',
    gradient: 'from-rose-500/20 to-pink-500/10',
    border: 'border-rose-500/30',
    accent: 'text-rose-400',
    heroLine: 'Your hormones changed. Your strategy should too.',
    therapies: [
      { name: 'Hormone Replacement Therapy (HRT)', desc: 'Personalized bioidentical hormone support addressing estrogen, progesterone, and testosterone for comprehensive symptom relief.', highlight: 'Root cause approach' },
      { name: 'Nuvella', desc: 'Targeted hormone optimization designed for women navigating perimenopause and menopause with personalized protocols.', highlight: 'Women-specific formulation' },
      { name: "Women's Optimization Stack", desc: 'Hormone support combined with NAD+, sleep peptides, and metabolic support for a comprehensive approach to the menopausal transition.', highlight: 'Most comprehensive approach' },
    ],
    benefits: ['Hot flash and night sweat reduction', 'Improved sleep quality and duration', 'Better mood stability and reduced anxiety', 'Cognitive clarity restoration', 'Metabolism and body composition support', 'Bone density protection'],
    whoIsFor: ['Women in perimenopause experiencing symptom changes', 'Menopausal women seeking comprehensive support', 'Women over 40 dealing with fatigue, sleep, or weight changes', 'Those wanting hormone support integrated with other therapies'],
    blogKeywords: ['women', 'hormone', 'estrogen', 'perimenopause', 'menopause', 'nuvella'],
    ctaText: 'Explore Hormone Support',
    forecastGoal: 'hormones',
    metaTitle: "Women's Hormone Optimization: HRT & Nuvella | Bryan 2.0",
    metaDescription: "Personalized hormone optimization for women — HRT, Nuvella, and comprehensive support for perimenopause and menopause.",
  },
};

const TherapyCategoryPage = ({ category }) => {
  const data = THERAPY_DATA[category];
  const navigate = useNavigate();
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [showSchedule, setShowSchedule] = useState(false);
  const Icon = data.icon;

  useEffect(() => {
    fetch(`${API_URL}/api/blog/posts`)
      .then(res => res.json())
      .then(posts => {
        const matched = posts.filter(p => {
          const text = `${p.title} ${p.slug}`.toLowerCase();
          return data.blogKeywords.some(k => text.includes(k));
        });
        setRelatedPosts(matched.slice(0, 4));
      })
      .catch(() => {});
  }, [category, data.blogKeywords]);

  return (
    <div className="min-h-screen bg-[#020817] text-foreground" data-testid={`therapy-${category}`}>
      <Helmet>
        <title>{data.metaTitle}</title>
        <meta name="description" content={data.metaDescription} />
      </Helmet>

      {/* Nav */}
      <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center group-hover:border-cyan-500/50 transition-all">
              <span className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">B</span>
            </div>
            <span className="font-heading text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">Bryan 2.0</span>
          </Link>
          <nav className="flex items-center gap-4 sm:gap-6">
            <Link to="/stack-finder" className="text-slate-400 hover:text-cyan-400 transition-colors hidden sm:flex items-center gap-2"><Search className="h-4 w-4" /> Stacks</Link>
            <Link to="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors hidden sm:flex items-center gap-2"><BookOpen className="h-4 w-4" /> Blog</Link>
            <a href="sms:8016518363" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"><MessageSquare className="h-4 w-4" /> <span className="hidden sm:inline">Text Bryan</span></a>
            <Button onClick={() => navigate('/forecast')} size="sm" className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white border-0" data-testid="nav-forecast-btn">
              AI Forecast
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <div className={`absolute top-20 left-1/4 w-96 h-96 bg-${data.color}-500/5 rounded-full blur-[120px]`} />
          <div className={`absolute bottom-20 right-1/4 w-80 h-80 bg-${data.color}-500/8 rounded-full blur-[100px]`} />
        </div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-6">
              <Link to="/" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">Home</Link>
              <ChevronRight className="h-3 w-3 text-slate-600" />
              <span className="text-slate-500 text-sm">Therapies</span>
              <ChevronRight className="h-3 w-3 text-slate-600" />
              <span className={`${data.accent} text-sm font-medium`}>{data.title}</span>
            </div>
            <div className={`inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r ${data.gradient} ${data.border} border mb-8`}>
              <Icon className={`h-5 w-5 ${data.accent}`} />
              <span className={`text-sm font-medium ${data.accent}`}>{data.subtitle}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              {data.heroLine}
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              {data.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate(`/forecast?goal=${data.forecastGoal}`)}
                size="lg"
                className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white border-0 text-base px-8 py-6"
                data-testid="therapy-forecast-cta"
              >
                {data.ctaText} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => setShowSchedule(true)}
                variant="outline"
                size="lg"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 text-base px-8 py-6"
                data-testid="therapy-schedule-cta"
              >
                <Calendar className="mr-2 h-5 w-5" /> Talk to Bryan
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Therapies Section */}
      <section className="py-16 sm:py-20 border-t border-slate-800/30">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4 text-center">
              Available Therapies
            </h2>
            <p className="text-slate-400 text-center mb-12 max-w-xl mx-auto">
              All programs include licensed medical provider oversight and personalized protocols.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {data.therapies.map((therapy, idx) => (
              <motion.div
                key={therapy.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`relative p-6 rounded-2xl bg-gradient-to-b ${data.gradient} ${data.border} border backdrop-blur-sm`}
              >
                <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${data.accent} bg-slate-900/60 border ${data.border} mb-4`}>
                  {therapy.highlight}
                </div>
                <h3 className="text-lg font-heading font-bold text-white mb-3">{therapy.name}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{therapy.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + Who Is This For */}
      <section className="py-16 sm:py-20 bg-slate-900/30 border-t border-slate-800/30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-2xl font-heading font-bold text-white mb-6 flex items-center gap-3">
                <Sparkles className={`h-6 w-6 ${data.accent}`} /> Benefits
              </h2>
              <ul className="space-y-4">
                {data.benefits.map(b => (
                  <li key={b} className="flex items-start gap-3">
                    <CheckCircle2 className={`h-5 w-5 ${data.accent} mt-0.5 flex-shrink-0`} />
                    <span className="text-slate-300 text-sm">{b}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-2xl font-heading font-bold text-white mb-6 flex items-center gap-3">
                <Star className={`h-6 w-6 ${data.accent}`} /> Who This Is For
              </h2>
              <ul className="space-y-4">
                {data.whoIsFor.map(w => (
                  <li key={w} className="flex items-start gap-3">
                    <ChevronRight className={`h-5 w-5 ${data.accent} mt-0.5 flex-shrink-0`} />
                    <span className="text-slate-300 text-sm">{w}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 sm:py-20 border-t border-slate-800/30">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-heading font-bold text-white mb-8 text-center">
              Related Articles
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedPosts.map(post => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group p-4 rounded-xl bg-slate-900/50 border border-slate-800/50 hover:border-slate-700/80 transition-all"
                  data-testid={`related-post-${post.slug}`}
                >
                  <h3 className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-xs text-slate-500 line-clamp-2">{post.excerpt}</p>
                  <span className="text-xs text-cyan-500 mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read more <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/blog" className="text-sm text-cyan-500 hover:text-cyan-400 transition-colors">
                View all articles →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 sm:py-20 border-t border-slate-800/30">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Take the free AI Forecast to see which therapies match your goals, or text Bryan directly for a personal conversation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate(`/forecast?goal=${data.forecastGoal}`)}
                size="lg"
                className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white border-0 px-8 py-6"
                data-testid="therapy-bottom-forecast-cta"
              >
                {data.ctaText} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 px-8 py-6"
              >
                <a href="sms:8016518363">
                  <MessageSquare className="mr-2 h-5 w-5" /> Text Bryan: 801-651-8363
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pt-12 pb-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-heading text-sm font-bold text-white mb-4 uppercase tracking-wider">Therapies</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><Link to="/therapies/weight-loss" className="hover:text-cyan-400 transition-colors">Weight Loss</Link></li>
                <li><Link to="/therapies/energy" className="hover:text-cyan-400 transition-colors">Energy & Cellular</Link></li>
                <li><Link to="/therapies/recovery" className="hover:text-cyan-400 transition-colors">Recovery & Healing</Link></li>
                <li><Link to="/therapies/body-composition" className="hover:text-cyan-400 transition-colors">Body Composition</Link></li>
                <li><Link to="/therapies/cognitive" className="hover:text-cyan-400 transition-colors">Cognitive</Link></li>
                <li><Link to="/therapies/sleep" className="hover:text-cyan-400 transition-colors">Sleep</Link></li>
                <li><Link to="/therapies/hormones" className="hover:text-cyan-400 transition-colors">Women's Hormones</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-sm font-bold text-white mb-4 uppercase tracking-wider">Tools</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><Link to="/forecast" className="hover:text-cyan-400 transition-colors">AI Forecast</Link></li>
                <li><Link to="/stack-finder" className="hover:text-cyan-400 transition-colors">Stack Finder</Link></li>
                <li><Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link></li>
                <li><Link to="/about" className="hover:text-cyan-400 transition-colors">About Bryan</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-sm font-bold text-white mb-4 uppercase tracking-wider">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><a href="sms:8016518363" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><MessageSquare className="h-3 w-3" /> Text: 801-651-8363</a></li>
                <li><a href="https://calendly.com/bryanm8623/30min" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Calendar className="h-3 w-3" /> Book a Call</a></li>
                <li><a href="mailto:Bryan@DirectMeds.com" className="hover:text-cyan-400 transition-colors">Bryan@DirectMeds.com</a></li>
              </ul>
            </div>
          </div>
          <div className="text-center text-xs text-slate-600 border-t border-white/5 pt-8 space-y-3">
            <p>All information on this site is educational and does not constitute medical advice. Results vary. All programs include licensed medical provider oversight.</p>
            <p>&copy; 2026 Bryan 2.0 Forecast. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <ScheduleModal isOpen={showSchedule} onClose={() => setShowSchedule(false)} source={`therapy_${category}`} />
    </div>
  );
};

// Export individual page components
export const WeightLossTherapy = () => <TherapyCategoryPage category="weight-loss" />;
export const EnergyTherapy = () => <TherapyCategoryPage category="energy" />;
export const RecoveryTherapy = () => <TherapyCategoryPage category="recovery" />;
export const BodyCompositionTherapy = () => <TherapyCategoryPage category="body-composition" />;
export const CognitiveTherapy = () => <TherapyCategoryPage category="cognitive" />;
export const SleepTherapy = () => <TherapyCategoryPage category="sleep" />;
export const HormonesTherapy = () => <TherapyCategoryPage category="hormones" />;

export default TherapyCategoryPage;
